/////////////////////////////////////////////////////////////////////
// Two-level category structure: Category -> Sub-category -> Size. //
/////////////////////////////////////////////////////////////////////

// The marketplace has a single flat 'category' listing field (managed in Console).
// Some of those categories also have sub-categories, and each sub-category has
// its own list of sizes. Some categories also ask for the colour of the item.
// Console cannot express those dependencies: it can hold the option lists, but it
// can't say "show these options only when the parent is X". That rule lives here,
// and it is applied in 3 places:
//   1. EditListingDetailsForm  (seller picks category -> sub-category -> size,
//                               and a colour where the category asks for one)
//   2. ListingPage             (buyer sees the saved values)
//   3. SearchPage filters      (buyer narrows down listings)
//
// The list of main categories lives here too, so that a new one works before
// Console is updated and so the list always reads in alphabetical order.
//
// Console setup that matches this file:
//   - listing field 'category',    schema "Select one" (enum), used as a filter
//   - listing field 'subCategory', schema "Select one" (enum), used as a filter
//   - listing field 'size',        schema "Select one" (enum), used as a filter
//   - listing field 'colors',      schema "Select one" (enum), used as a filter
// Option keys must be unique inside one field, but labels may repeat. Size "4"
// exists in three different lists, so keys are prefixed ('clothing-4', 'kids-4')
// while the label the user sees is just "4". Dots are not used in keys because
// the value ends up in the search URL (e.g. ?pub_size=shoes-3-5).
//
// The option lists (and this rule's data) live in ./categoryFieldOptions, a
// plain CommonJS module, so the server-side partner API can `require()` the
// exact same data instead of duplicating it (frontend-only ES modules like
// this one cannot be `require()`-d from plain Node).

import {
  CATEGORY_KEY,
  SUB_CATEGORY_KEY,
  SIZE_KEY,
  COLOUR_KEY,
  subCategoryOptionsByCategory,
  sizeOptionsBySubCategory,
  colourOptionsByCategory,
  categoryOptions,
  CATEGORY_OPTION_LAST,
  mergeWithHostedOptions,
} from './categoryFieldOptions';

export {
  CATEGORY_KEY,
  SUB_CATEGORY_KEY,
  SIZE_KEY,
  COLOUR_KEY,
  subCategoryOptionsByCategory,
  sizeOptionsBySubCategory,
  colourOptionsByCategory,
};

// Flatten a "value or comma separated values or array" selection into an array.
// The search URL can hold several values at once (e.g. ?pub_category=fashion,books).
const toArray = value =>
  value == null
    ? []
    : Array.isArray(value)
    ? value
    : `${value}`
        .replace(/^(has_all|has_any):/, '')
        .split(',')
        .filter(v => v.length > 0);

// Options of every group named in `keys`, in the order the groups are listed.
const optionsFor = (optionsByGroup, keys) =>
  toArray(keys).reduce((options, key) => {
    const groupOptions = optionsByGroup[key] || [];
    const newOnes = groupOptions.filter(o => !options.some(picked => picked.option === o.option));
    return [...options, ...newOnes];
  }, []);

/**
 * Sub-category options allowed for the given main category (or categories).
 * @param {String|Array} categories one category key, a comma separated list, or an array
 * @returns {Array} array of { option, label }
 */
export const subCategoryOptionsFor = categories =>
  optionsFor(subCategoryOptionsByCategory, categories);

/**
 * Size options allowed for the given sub-category (or sub-categories).
 * @param {String|Array} subCategories one sub-category key, a comma separated list, or an array
 * @returns {Array} array of { option, label }
 */
export const sizeOptionsFor = subCategories => optionsFor(sizeOptionsBySubCategory, subCategories);

/**
 * Colour options allowed for the given main category (or categories).
 * @param {String|Array} categories one category key, a comma separated list, or an array
 * @returns {Array} array of { option, label }
 */
export const colourOptionsFor = categories => optionsFor(colourOptionsByCategory, categories);

/**
 * Every sub-category / size option that exists, across all groups.
 * Used when nothing is selected yet (e.g. an unfiltered search page).
 */
export const allSubCategoryOptions = () =>
  subCategoryOptionsFor(Object.keys(subCategoryOptionsByCategory));
export const allSizeOptions = () => sizeOptionsFor(Object.keys(sizeOptionsBySubCategory));
export const allColourOptions = () => colourOptionsFor(Object.keys(colourOptionsByCategory));

/**
 * The main category options, in the order they should be shown everywhere:
 * alphabetical, with 'Other' last.
 *
 * Console's label wins whenever Console knows the option, so a label edited
 * there still reaches the app. A category that only Console knows about is kept
 * as well — nothing is ever dropped from the list.
 *
 * @param {Array} hostedOptions the Console field's enumOptions (may be missing)
 * @returns {Array} array of { option, label }
 */
export const categoryOptionsFrom = (hostedOptions = []) => {
  const hosted = hostedOptions || [];
  const known = categoryOptions.map(ownOption => {
    const match = hosted.find(o => `${o.option}` === ownOption.option);
    return match?.label ? { option: ownOption.option, label: `${match.label}` } : ownOption;
  });
  const onlyInConsole = hosted
    .filter(o => !categoryOptions.some(ownOption => ownOption.option === `${o.option}`))
    .map(o => ({ option: `${o.option}`, label: `${o.label || o.option}` }));

  const all = [...known, ...onlyInConsole];
  const sorted = all
    .filter(o => o.option !== CATEGORY_OPTION_LAST)
    .sort((a, b) => a.label.localeCompare(b.label, 'en'));
  return [...sorted, ...all.filter(o => o.option === CATEGORY_OPTION_LAST)];
};

/**
 * Every category key, in the order above. Handy where the labels are not needed,
 * e.g. checking the 'category' column of a bulk upload file.
 *
 * @returns {Array<String>}
 */
export const categoryKeys = () => categoryOptionsFrom().map(o => o.option);

/**
 * Give the category field the option list above, and leave every other field
 * untouched. This runs once, where the hosted config is read, so that listing
 * creation, the listing page, the search filters and the filter chips all see
 * the same categories in the same order.
 *
 * @param {Array} listingFields config.listing.listingFields
 * @returns {Array} the same entries, with the category field's options replaced
 */
export const withCategoryOptions = (listingFields = []) =>
  (listingFields || []).map(field =>
    field?.key === CATEGORY_KEY
      ? { ...field, enumOptions: categoryOptionsFrom(field.enumOptions) }
      : field
  );

/**
 * Narrow a listing field config so that a dependent field only offers the
 * options that belong to the current selection.
 *
 * Returns the config unchanged for every field that is not dependent,
 * and `null` for a dependent field that has nothing to offer — the caller
 * should then not render it at all.
 *
 * @param {Object} fieldConfig one entry of config.listing.listingFields
 * @param {Object} selection { category, subCategory } — each may be a key,
 *                 a comma separated list, or an array of keys
 * @returns {Object|null}
 */
export const dependentFieldConfig = (fieldConfig, selection = {}) => {
  const { key } = fieldConfig || {};

  if (key !== SUB_CATEGORY_KEY && key !== SIZE_KEY && key !== COLOUR_KEY) {
    return fieldConfig;
  }

  // Sub-category and colour both hang off the main category; size hangs off the
  // sub-category.
  const allowedOptions =
    key === SUB_CATEGORY_KEY
      ? subCategoryOptionsFor(selection.category)
      : key === COLOUR_KEY
      ? colourOptionsFor(selection.category)
      : sizeOptionsFor(selection.subCategory);

  if (allowedOptions.length === 0) {
    return null;
  }

  return {
    ...fieldConfig,
    // A single item has exactly one size and one colour, so these are always
    // single-choice fields even if Console still has one configured as
    // multi-select. The search sidebar can still offer several values at once —
    // an 'enum' listing field is rendered there with SelectMultipleFilter.
    schemaType: 'enum',
    enumOptions: mergeWithHostedOptions(allowedOptions, fieldConfig.enumOptions),
  };
};

// Keep only the values that the parent choice still allows.
// Returns null when nothing is left, so the caller can drop the param.
const keepAllowedValues = (value, allowedOptions) => {
  const allowed = allowedOptions.map(o => o.option);
  const kept = toArray(value).filter(v => allowed.includes(v));
  return kept.length > 0 ? kept.join(',') : null;
};

/**
 * Remove search params that the current selection no longer allows.
 *
 * A sub-category only exists under its category, and a size only exists under
 * its sub-category. Without this, a visitor who picks Fashion > Shoes > size 8
 * and then switches to Books would keep filtering by a shoe size they can no
 * longer see or clear. The same happens with a shared link.
 *
 * @param {Object} searchParams e.g. { pub_category: 'fashion', pub_size: 'shoes-8' }
 * @returns {Object} the same params, minus the values that are now impossible
 */
export const clearImpossibleCategoryParams = (searchParams = {}) => {
  const subCategoryParam = `pub_${SUB_CATEGORY_KEY}`;
  const sizeParam = `pub_${SIZE_KEY}`;
  const colourParam = `pub_${COLOUR_KEY}`;

  const hasSubCategory = searchParams[subCategoryParam] != null;
  const hasSize = searchParams[sizeParam] != null;
  const hasColour = searchParams[colourParam] != null;
  if (!hasSubCategory && !hasSize && !hasColour) {
    return searchParams;
  }

  const category = searchParams[`pub_${CATEGORY_KEY}`];
  const subCategory = keepAllowedValues(
    searchParams[subCategoryParam],
    subCategoryOptionsFor(category)
  );
  const size = keepAllowedValues(searchParams[sizeParam], sizeOptionsFor(subCategory));
  const colour = keepAllowedValues(searchParams[colourParam], colourOptionsFor(category));

  const cleaned = { ...searchParams };
  const keep = (param, value) => {
    if (value) {
      cleaned[param] = value;
    } else {
      delete cleaned[param];
    }
  };
  keep(subCategoryParam, subCategory);
  keep(sizeParam, size);
  keep(colourParam, colour);
  return cleaned;
};

/**
 * Put the dependent fields directly after the field they depend on, so the
 * seller and the buyer read them top to bottom: Category, Sub-category, Size,
 * Colour. Console decides the order of every other field.
 *
 * @param {Array} listingFields config.listing.listingFields
 * @returns {Array} the same entries, reordered
 */
export const orderCategoryFields = listingFields => {
  const chain = [CATEGORY_KEY, SUB_CATEGORY_KEY, SIZE_KEY, COLOUR_KEY];
  const inChain = (listingFields || []).filter(f => chain.includes(f.key));
  if (inChain.length === 0) {
    return listingFields || [];
  }

  const ordered = chain.map(key => inChain.find(f => f.key === key)).filter(Boolean);
  const rest = (listingFields || []).filter(f => !chain.includes(f.key));
  const firstChainIndex = listingFields.findIndex(f => chain.includes(f.key));
  const restBefore = rest.filter(f => listingFields.indexOf(f) < firstChainIndex);
  const restAfter = rest.filter(f => listingFields.indexOf(f) > firstChainIndex);

  return [...restBefore, ...ordered, ...restAfter];
};
